import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Snackbar,
  InputAdornment,
} from '@mui/material';
import {
  Storage as StorageIcon,
  CloudUpload as BandwidthIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  RestartAlt as ResetIcon,
} from '@mui/icons-material';
import api from '../services/api';

/**
 * Tier Limits Page
 * Allows admins to view and modify storage/bandwidth limits per subscription tier
 */
const PricingConfiguration = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Edit states
  const [editingStorage, setEditingStorage] = useState(false);
  const [editingBandwidth, setEditingBandwidth] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  
  // Form values
  const [storageFreeAllowances, setStorageFreeAllowances] = useState({ free: 0, standard: 0, professional: 0, enterprise: 0 });
  const [storageTiers, setStorageTiers] = useState([]);
  const [storageWarning, setStorageWarning] = useState(80);
  const [storageCritical, setStorageCritical] = useState(95);
  
  const [bandwidthFreeAllowances, setBandwidthFreeAllowances] = useState({ free: 0, standard: 0, professional: 0, enterprise: 0 });
  const [bandwidthTiers, setBandwidthTiers] = useState([]);
  const [bandwidthWarning, setBandwidthWarning] = useState(80);
  const [bandwidthCritical, setBandwidthCritical] = useState(95);

  useEffect(() => {
    fetchPricingConfig();
  }, []);

  const fetchPricingConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/pricing');
      if (response.data.success) {
        const { config } = response.data;
        const normalizedStorageAllowances = {
          ...config.storage.freeAllowances,
          standard: config.storage.freeAllowances?.standard ?? config.storage.freeAllowances?.professional ?? 0
        };
        const normalizedBandwidthAllowances = {
          ...config.bandwidth.freeAllowances,
          standard: config.bandwidth.freeAllowances?.standard ?? config.bandwidth.freeAllowances?.professional ?? 0
        };
        config.storage.freeAllowances = normalizedStorageAllowances;
        config.bandwidth.freeAllowances = normalizedBandwidthAllowances;
        setConfig(config);
        
        // Set form values
        setStorageFreeAllowances(normalizedStorageAllowances);
        setStorageTiers(config.storage.tiers);
        setStorageWarning(config.storage.warningThreshold);
        setStorageCritical(config.storage.criticalThreshold);
        
        setBandwidthFreeAllowances(normalizedBandwidthAllowances);
        setBandwidthTiers(config.bandwidth.tiers);
        setBandwidthWarning(config.bandwidth.warningThreshold);
        setBandwidthCritical(config.bandwidth.criticalThreshold);
      }
    } catch (err) {
      console.error('Error fetching tier limits config:', err);
      setError(err.message || 'Failed to fetch tier limits configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStorage = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await api.put('/pricing', {
        storage: {
          freeAllowances: storageFreeAllowances,
          tiers: storageTiers,
          warningThreshold: storageWarning,
          criticalThreshold: storageCritical
        }
      });
      
      if (response.data.success) {
        setSuccess('Storage limits updated successfully');
        setEditingStorage(false);
        fetchPricingConfig();
      }
    } catch (err) {
      setError(err.message || 'Failed to update storage limits');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveBandwidth = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await api.put('/pricing', {
        bandwidth: {
          freeAllowances: bandwidthFreeAllowances,
          tiers: bandwidthTiers,
          warningThreshold: bandwidthWarning,
          criticalThreshold: bandwidthCritical
        }
      });
      
      if (response.data.success) {
        setSuccess('Bandwidth limits updated successfully');
        setEditingBandwidth(false);
        fetchPricingConfig();
      }
    } catch (err) {
      setError(err.message || 'Failed to update bandwidth limits');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await api.post('/pricing/reset');
      if (response.data.success) {
        setSuccess('Limits reset to defaults');
        setResetDialogOpen(false);
        fetchPricingConfig();
      }
    } catch (err) {
      setError(err.message || 'Failed to reset limits');
    } finally {
      setSaving(false);
    }
  };

  const updateStorageTier = (index, field, value) => {
    const newTiers = [...storageTiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setStorageTiers(newTiers);
  };

  const updateBandwidthTier = (index, field, value) => {
    const newTiers = [...bandwidthTiers];
    newTiers[index] = { ...newTiers[index], [field]: value };
    setBandwidthTiers(newTiers);
  };

  const formatGB = (gb) => {
    if (gb === null || gb === undefined) return 'Unlimited';
    if (gb < 1) return `${(gb * 1024).toFixed(0)} MB`;
    return `${gb} GB`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2">
          <strong>Novo-hosted AI</strong> (monthly credits, pooled models, BYOK defaults) is under{' '}
          <Button
            component={RouterLink}
            to="/configuration?tab=features"
            variant="contained"
            size="small"
            sx={{ mx: 0.5, verticalAlign: 'middle' }}
          >
            Configuration → Features &amp; Limits
          </Button>
          . This page is only storage/bandwidth tier limits.
        </Typography>
      </Alert>

      {/* Header Actions */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchPricingConfig}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<ResetIcon />}
            onClick={() => setResetDialogOpen(true)}
          >
            Reset to Defaults
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Version Info */}
      {config && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Version {config.version} | Last updated: {new Date(config.lastUpdatedAt).toLocaleString()}
          {config.lastUpdatedBy && ` by ${config.lastUpdatedBy}`}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Storage Limits */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StorageIcon color="primary" />
                  Storage Limits
                </Typography>
                {editingStorage ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditingStorage(false);
                        // Reset to original values
                        if (config) {
                          setStorageFreeAllowances(config.storage.freeAllowances);
                          setStorageTiers(config.storage.tiers);
                          setStorageWarning(config.storage.warningThreshold);
                          setStorageCritical(config.storage.criticalThreshold);
                        }
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveStorage}
                      disabled={saving}
                    >
                      Save
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => setEditingStorage(true)}
                  >
                    Edit
                  </Button>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Free Allowances */}
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Storage Allowances by Tier
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Free Tier"
                    type="number"
                    value={storageFreeAllowances.free}
                    onChange={(e) => setStorageFreeAllowances({
                      ...storageFreeAllowances,
                      free: parseFloat(e.target.value) || 0
                    })}
                    disabled={!editingStorage}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">GB</InputAdornment>
                    }}
                    helperText={formatGB(storageFreeAllowances.free)}
                    inputProps={{ step: 0.1, min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Standard Tier"
                    type="number"
                    value={storageFreeAllowances.standard}
                    onChange={(e) => setStorageFreeAllowances({
                      ...storageFreeAllowances,
                      standard: parseFloat(e.target.value) || 0
                    })}
                    disabled={!editingStorage}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">GB</InputAdornment>
                    }}
                    helperText={formatGB(storageFreeAllowances.standard)}
                    inputProps={{ step: 0.5, min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Professional Tier"
                    type="number"
                    value={storageFreeAllowances.professional}
                    onChange={(e) => setStorageFreeAllowances({
                      ...storageFreeAllowances,
                      professional: parseFloat(e.target.value) || 0
                    })}
                    disabled={!editingStorage}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">GB</InputAdornment>
                    }}
                    helperText={formatGB(storageFreeAllowances.professional)}
                    inputProps={{ step: 0.5, min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Enterprise Tier"
                    type="number"
                    value={storageFreeAllowances.enterprise}
                    onChange={(e) => setStorageFreeAllowances({
                      ...storageFreeAllowances,
                      enterprise: parseFloat(e.target.value) || 0
                    })}
                    disabled={!editingStorage}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">GB</InputAdornment>
                    }}
                    helperText={formatGB(storageFreeAllowances.enterprise)}
                    inputProps={{ step: 1, min: 0 }}
                  />
                </Grid>
              </Grid>

              {/* Overage Tiers Table */}
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Overage Tiers
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tier Name</TableCell>
                      <TableCell>Min GB</TableCell>
                      <TableCell>Max GB</TableCell>
                      <TableCell>Price per GB</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {storageTiers.map((tier, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {editingStorage ? (
                            <TextField
                              size="small"
                              value={tier.tierName}
                              onChange={(e) => updateStorageTier(index, 'tierName', e.target.value)}
                            />
                          ) : (
                            <Chip label={tier.tierName} size="small" />
                          )}
                        </TableCell>
                        <TableCell>
                          {editingStorage ? (
                            <TextField
                              size="small"
                              type="number"
                              value={tier.minGB}
                              onChange={(e) => updateStorageTier(index, 'minGB', parseFloat(e.target.value) || 0)}
                              sx={{ width: 80 }}
                            />
                          ) : (
                            formatGB(tier.minGB)
                          )}
                        </TableCell>
                        <TableCell>
                          {editingStorage ? (
                            <TextField
                              size="small"
                              type="number"
                              value={tier.maxGB === null ? '' : tier.maxGB}
                              onChange={(e) => updateStorageTier(index, 'maxGB', e.target.value === '' ? null : parseFloat(e.target.value))}
                              placeholder="Unlimited"
                              sx={{ width: 100 }}
                            />
                          ) : (
                            formatGB(tier.maxGB)
                          )}
                        </TableCell>
                        <TableCell>
                          {editingStorage ? (
                            <TextField
                              size="small"
                              type="number"
                              value={tier.pricePerGB}
                              onChange={(e) => updateStorageTier(index, 'pricePerGB', parseFloat(e.target.value) || 0)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                              }}
                              sx={{ width: 100 }}
                              inputProps={{ step: 0.01, min: 0 }}
                            />
                          ) : (
                            `$${tier.pricePerGB.toFixed(2)}`
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Warning Thresholds */}
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Warning Thresholds
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Warning Threshold"
                    type="number"
                    value={storageWarning}
                    onChange={(e) => setStorageWarning(parseFloat(e.target.value) || 0)}
                    disabled={!editingStorage}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                    helperText="Shows warning when usage exceeds this %"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Critical Threshold"
                    type="number"
                    value={storageCritical}
                    onChange={(e) => setStorageCritical(parseFloat(e.target.value) || 0)}
                    disabled={!editingStorage}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                    helperText="Shows critical alert when usage exceeds this %"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Bandwidth Limits */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <BandwidthIcon color="secondary" />
                  Bandwidth Limits
                </Typography>
                {editingBandwidth ? (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setEditingBandwidth(false);
                        // Reset to original values
                        if (config) {
                          setBandwidthFreeAllowances(config.bandwidth.freeAllowances);
                          setBandwidthTiers(config.bandwidth.tiers);
                          setBandwidthWarning(config.bandwidth.warningThreshold);
                          setBandwidthCritical(config.bandwidth.criticalThreshold);
                        }
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<SaveIcon />}
                      onClick={handleSaveBandwidth}
                      disabled={saving}
                    >
                      Save
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    color="secondary"
                    startIcon={<EditIcon />}
                    onClick={() => setEditingBandwidth(true)}
                  >
                    Edit
                  </Button>
                )}
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Free Allowances */}
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Bandwidth Allowances by Tier
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Free Tier"
                    type="number"
                    value={bandwidthFreeAllowances.free}
                    onChange={(e) => setBandwidthFreeAllowances({
                      ...bandwidthFreeAllowances,
                      free: parseFloat(e.target.value) || 0
                    })}
                    disabled={!editingBandwidth}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">GB</InputAdornment>
                    }}
                    helperText={formatGB(bandwidthFreeAllowances.free)}
                    inputProps={{ step: 1, min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Standard Tier"
                    type="number"
                    value={bandwidthFreeAllowances.standard}
                    onChange={(e) => setBandwidthFreeAllowances({
                      ...bandwidthFreeAllowances,
                      standard: parseFloat(e.target.value) || 0
                    })}
                    disabled={!editingBandwidth}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">GB</InputAdornment>
                    }}
                    helperText={formatGB(bandwidthFreeAllowances.standard)}
                    inputProps={{ step: 5, min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Professional Tier"
                    type="number"
                    value={bandwidthFreeAllowances.professional}
                    onChange={(e) => setBandwidthFreeAllowances({
                      ...bandwidthFreeAllowances,
                      professional: parseFloat(e.target.value) || 0
                    })}
                    disabled={!editingBandwidth}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">GB</InputAdornment>
                    }}
                    helperText={formatGB(bandwidthFreeAllowances.professional)}
                    inputProps={{ step: 5, min: 0 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <TextField
                    label="Enterprise Tier"
                    type="number"
                    value={bandwidthFreeAllowances.enterprise}
                    onChange={(e) => setBandwidthFreeAllowances({
                      ...bandwidthFreeAllowances,
                      enterprise: parseFloat(e.target.value) || 0
                    })}
                    disabled={!editingBandwidth}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">GB</InputAdornment>
                    }}
                    helperText={formatGB(bandwidthFreeAllowances.enterprise)}
                    inputProps={{ step: 10, min: 0 }}
                  />
                </Grid>
              </Grid>

              {/* Overage Tiers Table */}
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Overage Tiers
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tier Name</TableCell>
                      <TableCell>Min GB</TableCell>
                      <TableCell>Max GB</TableCell>
                      <TableCell>Price per GB</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bandwidthTiers.map((tier, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          {editingBandwidth ? (
                            <TextField
                              size="small"
                              value={tier.tierName}
                              onChange={(e) => updateBandwidthTier(index, 'tierName', e.target.value)}
                            />
                          ) : (
                            <Chip label={tier.tierName} size="small" color="secondary" variant="outlined" />
                          )}
                        </TableCell>
                        <TableCell>
                          {editingBandwidth ? (
                            <TextField
                              size="small"
                              type="number"
                              value={tier.minGB}
                              onChange={(e) => updateBandwidthTier(index, 'minGB', parseFloat(e.target.value) || 0)}
                              sx={{ width: 80 }}
                            />
                          ) : (
                            formatGB(tier.minGB)
                          )}
                        </TableCell>
                        <TableCell>
                          {editingBandwidth ? (
                            <TextField
                              size="small"
                              type="number"
                              value={tier.maxGB === null ? '' : tier.maxGB}
                              onChange={(e) => updateBandwidthTier(index, 'maxGB', e.target.value === '' ? null : parseFloat(e.target.value))}
                              placeholder="Unlimited"
                              sx={{ width: 100 }}
                            />
                          ) : (
                            formatGB(tier.maxGB)
                          )}
                        </TableCell>
                        <TableCell>
                          {editingBandwidth ? (
                            <TextField
                              size="small"
                              type="number"
                              value={tier.pricePerGB}
                              onChange={(e) => updateBandwidthTier(index, 'pricePerGB', parseFloat(e.target.value) || 0)}
                              InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>
                              }}
                              sx={{ width: 100 }}
                              inputProps={{ step: 0.01, min: 0 }}
                            />
                          ) : (
                            `$${tier.pricePerGB.toFixed(2)}`
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Warning Thresholds */}
              <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                Warning Thresholds
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Warning Threshold"
                    type="number"
                    value={bandwidthWarning}
                    onChange={(e) => setBandwidthWarning(parseFloat(e.target.value) || 0)}
                    disabled={!editingBandwidth}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                    helperText="Shows warning when usage exceeds this %"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Critical Threshold"
                    type="number"
                    value={bandwidthCritical}
                    onChange={(e) => setBandwidthCritical(parseFloat(e.target.value) || 0)}
                    disabled={!editingBandwidth}
                    fullWidth
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>
                    }}
                    helperText="Shows critical alert when usage exceeds this %"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reset Confirmation Dialog */}
      <Dialog open={resetDialogOpen} onClose={() => setResetDialogOpen(false)}>
        <DialogTitle>Reset Limits to Defaults?</DialogTitle>
        <DialogContent>
          <Typography>
            This will reset all storage and bandwidth limits to their default values.
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleReset} color="warning" variant="contained" disabled={saving}>
            Reset to Defaults
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PricingConfiguration;
